import { Form, PasswordField, TextField } from '@components/form-components';
import useFormState from '@hooks/useFormState';
import useTrackFormChanges from '@hooks/useTrackFormChanges';
import { Alert } from '@components/Alert';
import { Button, Link } from '@mui/material';
import React, { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { FormData, FormErrors } from './interfaces';
import { WindowPageTitle } from '@components/WindowPageTitle';
import { PanelTitle } from '../components/PanelTitle';
import { atLeast, isEmpty } from '@helpers/formHelpers';
import { enqueueSnackbar } from 'notistack';
import { useResetPassword } from '@reactQuery/auth';
import { IResetPasswordParams } from '@api/authApi/interfaces';
import { AxiosError } from 'axios';

export const ResetPassword: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');
  const { formData, setFormFieldData, setFormData } = useFormState<FormData>(
    {}
  );
  const { formErrors, setFormErrors } = useTrackFormChanges(formData);
  const { mutate: resetPassword, isPending: isResettingPassword } =
    useResetPassword();

  const validateForm = (): void => {
    const errors: FormErrors = {};

    if (!formData.password || isEmpty(formData.password)) {
      errors.password = t('errors.required');
    } else if (!atLeast(formData.password, 5)) {
      errors.password = t('errors.atLeast', {
        field: t('forms:login.labels.password').toLocaleLowerCase(),
        min: 5,
      });
    }

    if (!formData.retypePassword || isEmpty(formData.retypePassword)) {
      errors.retypePassword = t('errors.required');
    } else if (formData.retypePassword !== formData.password) {
      errors.retypePassword = t(
        'forms:fields.passwordField.errors.passwordsDoNotMatch'
      );
    }

    setFormErrors(errors);
  };

  const handleSubmit = () => {
    if (
      !Object.keys(formErrors).length &&
      token &&
      formData.password &&
      !isResettingPassword
    ) {
      const params: IResetPasswordParams = {
        token,
        password: formData.password,
      };

      resetPassword(params, {
        onError: (error) => {
          const axiosError = error as AxiosError;

          if (axiosError.response?.status === 404) {
            enqueueSnackbar(t('forms:resetPassword.messages.tokenNotValid'), {
              variant: 'error',
            });

            navigate('/login');
          } else if (axiosError.response?.status === 422) {
            enqueueSnackbar(t('forms:resetPassword.messages.tokenExpired'), {
              variant: 'error',
            });

            navigate('/login');
          } else {
            enqueueSnackbar(t('forms:resetPassword.messages.error'), {
              variant: 'error',
            });
          }
        },
        onSuccess: () => {
          enqueueSnackbar(t('forms:resetPassword.messages.success'), {
            variant: 'success',
          });

          navigate('/login');
        },
      });
    }
  };

  useEffect(() => {
    if (token === null) {
      navigate('/login');
    }
  }, [token, navigate]);

  return (
    <Fragment>
      <WindowPageTitle title={t('forms:resetPassword.title')} />
      <PanelTitle title={t('forms:resetPassword.title')} />
      <Alert severity="info" content={t('forms:resetPassword.info')} />

      <Form onSubmit={handleSubmit} id="reset-password-form">
        <PasswordField
          label={t('forms:resetPassword.labels.password')}
          placeholder={t('forms:resetPassword.placeholders.retypePassword')}
          error={formErrors.password}
          value={formData.password}
          onChange={(value) => setFormFieldData('password', value)}
          strengthMeter
        />
        <PasswordField
          label={t('forms:resetPassword.labels.retypePassword')}
          placeholder={t('forms:resetPassword.placeholders.retypePassword')}
          error={formErrors.retypePassword}
          value={formData.retypePassword}
          onChange={(value) => setFormFieldData('retypePassword', value)}
        />
      </Form>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        onClick={validateForm}
        disabled={isResettingPassword}
        form="reset-password-form"
      >
        {t('forms:resetPassword.buttons.reset')}
      </Button>

      <Link
        component="button"
        type="button"
        onClick={() => {
          navigate('/login');
        }}
        variant="body2"
        sx={{ alignSelf: 'center' }}
      >
        {t('forms:resetPassword.buttons.goToLogin')}
      </Link>
    </Fragment>
  );
};
